import { createSlice } from '@reduxjs/toolkit';

import type { PayloadAction } from '@reduxjs/toolkit';

export enum AnimationState {
  DID_ENTER = 'DID_ENTER',
  DID_LEAVE = 'DID_LEAVE',
  IS_ENTERING = 'IS_ENTERING',
  IS_LEAVING = 'IS_LEAVING',
  IS_PENDING = 'IS_PENDING',
  SHOULD_ENTER = 'SHOULD_ENTER',
  SHOULD_LEAVE = 'SHOULD_LEAVE',
}

const VALID_TRANSITIONS = {
  [AnimationState.DID_ENTER]: new Set([
    AnimationState.IS_PENDING,
    AnimationState.SHOULD_LEAVE,
  ]),
  [AnimationState.DID_LEAVE]: new Set([
    AnimationState.IS_PENDING,
    AnimationState.SHOULD_ENTER,
  ]),
  [AnimationState.IS_ENTERING]: new Set([
    AnimationState.DID_ENTER,
    AnimationState.SHOULD_LEAVE,
  ]),
  [AnimationState.IS_LEAVING]: new Set([
    AnimationState.DID_LEAVE,
    AnimationState.SHOULD_ENTER,
  ]),
  [AnimationState.IS_PENDING]: new Set([
    AnimationState.SHOULD_ENTER,
    AnimationState.SHOULD_LEAVE,
  ]),
  [AnimationState.SHOULD_ENTER]: new Set([
    AnimationState.DID_ENTER,
    AnimationState.IS_ENTERING,
    AnimationState.SHOULD_LEAVE,
  ]),
  [AnimationState.SHOULD_LEAVE]: new Set([
    AnimationState.DID_LEAVE,
    AnimationState.IS_LEAVING,
    AnimationState.SHOULD_ENTER,
  ]),
};

export const animationStates = createSlice({
  initialState: {} as Record<string, Record<string, AnimationState>>,
  name: 'animationStates',
  reducers: {
    add: (
      state,
      {
        payload,
      }: PayloadAction<{
        animationId: number;
        animationState: AnimationState.IS_PENDING | AnimationState.SHOULD_ENTER;
        id: string;
      }>,
    ) => {
      state[payload.id] = state[payload.id] ?? {};

      state[payload.id][payload.animationId] = payload.animationState;
    },
    remove: (
      state,
      {
        payload,
      }: PayloadAction<{
        animationId: number;
        id: string;
      }>,
    ) => {
      state[payload.id] = state[payload.id] ?? {};

      delete state[payload.id][payload.animationId];
    },
    set: (
      state,
      {
        payload,
      }: PayloadAction<{
        animationId: number;
        animationState: Exclude<
          AnimationState,
          | AnimationState.IS_PENDING
          | AnimationState.SHOULD_ENTER
          | AnimationState.SHOULD_LEAVE
        >;
        id: string;
      }>,
    ) => {
      state[payload.id] = state[payload.id] ?? {};

      const validTransitions =
        VALID_TRANSITIONS[state[payload.id][payload.animationId]];
      if (validTransitions && validTransitions.has(payload.animationState)) {
        state[payload.id][payload.animationId] = payload.animationState;
      } else if (
        state[payload.id][payload.animationId] !== payload.animationState
      ) {
        console.warn(
          `invalid transition from ${
            state[payload.id][payload.animationId]
          } -> ${payload.animationState}`,
        );
      }
    },
    setAll: (
      state,
      {
        payload,
      }: PayloadAction<{
        animationState:
          | AnimationState.SHOULD_ENTER
          | AnimationState.SHOULD_LEAVE;
        id: string;
      }>,
    ) => {
      state[payload.id] = state[payload.id] ?? {};

      Object.keys(state[payload.id]).forEach((animationId) => {
        if (
          VALID_TRANSITIONS[state[payload.id][animationId]].has(
            payload.animationState,
          )
        ) {
          state[payload.id][animationId] = payload.animationState;
        } else if (state[payload.id][animationId] !== payload.animationState) {
          console.warn(
            `invalid transition from ${state[payload.id][animationId]} -> ${
              payload.animationState
            }`,
          );
        }
      });
    },
  },
});
